import {
  useState,
  useRef,
  useEffect,
  useCallback,
  type MutableRefObject,
  type RefCallback,
} from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import styles from "./OutlineNode.module.css";

export interface OutlineNodeData {
  id: string;
  content: string;
  note?: string;
  children?: OutlineNodeData[];
  collapsed?: boolean;
  isAiGenerated?: boolean;
  /** Original stream message content, used to track sync provenance after edits */
  sourceStreamContent?: string;
  /** Controls how this node renders when exported to draft.
   *  "heading" (default) → section heading; "content" → body paragraph. */
  nodeType?: "heading" | "content";
}

interface OutlineNodeProps {
  node: OutlineNodeData;
  depth: number;
  onUpdate: (id: string, updates: Partial<OutlineNodeData>) => void;
  onDelete: (id: string) => void;
  onAddChild: (parentId: string) => void;
  onAddSibling: (siblingId: string, pendingUpdates?: Partial<OutlineNodeData>) => void;
  onExpandPoint?: (id: string) => void;
  onToggleCollapse: (id: string) => void;
  onIndent: (id: string) => void;
  onOutdent: (id: string) => void;
  onMoveUp: (id: string) => void;
  onMoveDown: (id: string) => void;
  isDropTarget?: boolean;
  isCollapsed?: boolean;
  isExpanding?: boolean;
  isZenMode?: boolean;
  totalNodeCount?: number;
  pendingFocusIdRef?: MutableRefObject<string | null>;
  nodeIds?: string[];
  registerInputRef?: (nodeId: string) => RefCallback<HTMLTextAreaElement>;
  focusNode?: (nodeId: string) => void;
  editingNodeId?: string | null;
  clearEditingNodeId?: (id: string | null) => void;
}

export function OutlineNode({
  node,
  depth,
  onUpdate,
  onDelete,
  onAddChild,
  onAddSibling,
  onExpandPoint,
  onToggleCollapse,
  onIndent,
  onOutdent,
  onMoveUp,
  onMoveDown,
  isDropTarget = false,
  isCollapsed = false,
  isExpanding = false,
  isZenMode = false,
  totalNodeCount = 0,
  pendingFocusIdRef,
  nodeIds = [],
  registerInputRef,
  focusNode,
  editingNodeId,
  clearEditingNodeId,
}: OutlineNodeProps) {
  const isNewlyCreated = pendingFocusIdRef?.current === node.id;
  const shouldAutoFocus = !node.content || isNewlyCreated;
  if (isNewlyCreated) {
    pendingFocusIdRef.current = null;
  }
  // Capture creation status in a ref so it survives StrictMode double-mount
  // (the ref is cleared during render, so a second mount would see false)
  const wasNewlyCreatedRef = useRef(isNewlyCreated);
  const [justCreated, setJustCreated] = useState(false);
  const [showActions, setShowActions] = useState(false);
  const [showOverflow, setShowOverflow] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isEditingNote, setIsEditingNote] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const noteInputRef = useRef<HTMLTextAreaElement>(null);
  const overflowRef = useRef<HTMLDivElement>(null);

  // Brief highlight animation when a new node is created via Enter
  useEffect(() => {
    if (wasNewlyCreatedRef.current) {
      setJustCreated(true);
      const timer = setTimeout(() => setJustCreated(false), 600);
      return () => clearTimeout(timer);
    }
  }, []);

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: node.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    "--depth": depth,
  } as React.CSSProperties;

  const autoResize = useCallback((el: HTMLTextAreaElement | null) => {
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, []);

  // Auto-resize textarea on mount so multi-line content displays fully.
  useEffect(() => {
    autoResize(inputRef.current);
  }, [autoResize, node.content]);

  useEffect(() => {
    if (shouldAutoFocus && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [shouldAutoFocus]);

  useEffect(() => {
    if (editingNodeId === node.id && inputRef.current) {
      inputRef.current.focus();
      clearEditingNodeId?.(null);
    }
  }, [editingNodeId, node.id, clearEditingNodeId]);

  useEffect(() => {
    if (!showOverflow) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      // When React re-renders in response to a click (e.g. showing the
      // delete-confirmation UI), the original click target may be unmounted
      // before this document-level listener fires.  An disconnected target
      // is by definition not "outside" the menu, so ignore it.
      if (overflowRef.current && target.isConnected && !overflowRef.current.contains(target)) {
        setShowOverflow(false);
        setShowDeleteConfirm(false);
      }
    };
    // Use 'click' instead of 'mousedown' to avoid racing with the toggle
    // button's onClick handler, which caused the menu to flicker or close
    // immediately after opening.
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [showOverflow]);

  const handleContentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    onUpdate(node.id, { content: e.target.value });
    autoResize(e.target);
  };

  const handleNoteChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    onUpdate(node.id, { note: e.target.value });
    autoResize(e.target);
  };

  const handleNoteBlur = () => {
    // If the note is empty after editing, clear it and close the editor
    if (!noteInputRef.current?.value?.trim()) {
      onUpdate(node.id, { note: undefined });
      setIsEditingNote(false);
    } else {
      setIsEditingNote(false);
    }
  };

  const handleNoteKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      noteInputRef.current?.blur();
    }
  };

  const startEditingNote = () => {
    setIsEditingNote(true);
    // Focus the note input after it renders
    requestAnimationFrame(() => {
      if (noteInputRef.current) {
        noteInputRef.current.focus();
        autoResize(noteInputRef.current);
      }
    });
  };

  // Track whether Enter was just pressed so we can suppress the blur-triggered
  // exit from editing mode (which would otherwise race with the sibling creation).
  const enterPressedRef = useRef(false);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      const currentContent = inputRef.current?.value ?? node.content;

      // If the node is truly empty (no title, no note, and no children),
      // just confirm/blur instead of creating another empty sibling —
      // prevents cluttering the outline with blank items. Nodes with a
      // populated note are allowed through so users can still add siblings
      // from note-backed items (#1362). Nodes with children are also
      // allowed through so untitled parent nodes keep normal keyboard flow.
      const hasKids = (node.children?.length ?? 0) > 0;
      if (!currentContent.trim() && !node.note?.trim() && !hasKids) {
        inputRef.current?.blur();
        return;
      }

      // Pass any pending content change to onAddSibling so the update and
      // sibling insertion are applied atomically in a single onChange call.
      const pendingUpdates =
        currentContent !== node.content ? { content: currentContent } : undefined;
      enterPressedRef.current = true;
      onAddSibling(node.id, pendingUpdates);
    } else if (e.key === "Escape") {
      inputRef.current?.blur();
    } else if (e.key === "Backspace" && !inputRef.current?.value) {
      // Delete empty node on Backspace and let focus flow naturally.
      // Skip if the node has a note body — the title may be empty
      // intentionally for long/multi-line imported items (#1362).
      // We intentionally don't check sourceStreamContent here because
      // short single-line imports still use normal title-based editing
      // and should support Backspace deletion when the title is cleared.
      if (!node.note) {
        e.preventDefault();
        onDelete(node.id);
      }
    } else if (e.key === "Tab") {
      e.preventDefault();
      if (e.shiftKey) {
        onOutdent(node.id);
      } else {
        onIndent(node.id);
      }
    } else if ((e.key === "ArrowUp" || e.key === "ArrowDown") && e.altKey) {
      e.preventDefault();
      if (e.key === "ArrowUp") {
        onMoveUp(node.id);
      } else {
        onMoveDown(node.id);
      }
    } else if ((e.key === "ArrowUp" || e.key === "ArrowDown") && focusNode && nodeIds.length > 0) {
      const idx = nodeIds.indexOf(node.id);
      if (idx === -1) return;
      const targetIdx = e.key === "ArrowUp" ? idx - 1 : idx + 1;
      if (targetIdx >= 0 && targetIdx < nodeIds.length) {
        e.preventDefault();
        focusNode(nodeIds[targetIdx]);
      }
    }
  };

  const handleBlur = (e: React.FocusEvent) => {
    // Reset the enter-pressed flag so subsequent blur events are clean.
    if (enterPressedRef.current) {
      enterPressedRef.current = false;
      return;
    }

    // If focus is moving to another element within this node's container
    // (e.g. overflow menu, collapse toggle, drag handle), don't auto-delete.
    // blur fires before click, so deleting here would prevent those controls
    // from receiving their click events.
    const container = (e.currentTarget as HTMLElement)?.closest(`.${styles.node}`);
    if (container && e.relatedTarget instanceof Node && container.contains(e.relatedTarget)) {
      return;
    }

    // Auto-remove empty sections on blur to keep the outline tidy.
    // Skip if the node has children or content, or if it's the last node.
    const hasContent = (inputRef.current?.value ?? node.content).trim().length > 0;
    const hasKids = node.children && node.children.length > 0;
    // Only protect nodes with a note body from auto-delete — short
    // single-line imports carry sourceStreamContent but use a real title,
    // so they should follow normal cleanup behavior.
    if (!hasContent && !hasKids && !node.note && (totalNodeCount ?? 0) > 1) {
      onDelete(node.id);
    }
  };

  const hasChildren = node.children && node.children.length > 0;
  const isEmpty = !node.content?.trim() && !hasChildren && !node.note;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`${styles.node} ${isDragging ? styles.dragging : ""} ${
        isDropTarget ? styles.dropTarget : ""
      } ${isZenMode ? styles.zenMode : ""} ${justCreated ? styles.justCreated : ""}`}
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => {
        // Keep actions visible while the overflow menu is open so users
        // don't lose the menu when the cursor drifts outside the node.
        if (!showOverflow) setShowActions(false);
      }}
    >
      {/* Tree connecting lines for nested items */}
      {!isZenMode && depth > 0 && (
        <>
          <span
            className={`${styles.treeLine} ${styles.treeLineVertical}`}
            style={{ "--tree-line-left": depth * 28 + 8 - 12 } as React.CSSProperties}
          />
          <span
            className={`${styles.treeLine} ${styles.treeLineBranch}`}
            style={{ "--tree-line-left": depth * 28 + 8 - 12 } as React.CSSProperties}
          />
        </>
      )}
      <div className={styles.nodeInner} style={{ paddingLeft: `${depth * 28 + 8}px` }}>
        {!isZenMode && totalNodeCount > 1 && (
          <button
            type="button"
            className={styles.dragHandle}
            {...attributes}
            {...listeners}
            aria-label="Drag to reorder"
          >
            <GripIcon />
          </button>
        )}

        {hasChildren ? (
          <button
            type="button"
            className={`${styles.collapseButton} ${isCollapsed ? styles.collapsed : ""} ${isZenMode ? styles.collapseButtonZen : ""}`}
            onClick={() => onToggleCollapse(node.id)}
            aria-label={isCollapsed ? "Expand" : "Collapse"}
          >
            <ChevronIcon />
          </button>
        ) : (
          !isZenMode && <div className={styles.collapseSpacer} />
        )}

        <div
          className={`${styles.contentWrapper} ${
            depth === 0
              ? styles.contentDepth0
              : depth === 1
                ? styles.contentDepth1
                : styles.contentDepth2
          }`}
        >
          <textarea
            ref={(el) => {
              (inputRef as React.MutableRefObject<HTMLTextAreaElement | null>).current = el;
              registerInputRef?.(node.id)(el);
            }}
            className={styles.input}
            value={node.content}
            onChange={handleContentChange}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            placeholder={depth === 0 ? "Section title…" : depth === 1 ? "Sub-point…" : "Detail…"}
            rows={1}
          />

          {node.note && !isEditingNote && (
            <CollapsibleNote text={node.note} onEdit={startEditingNote} />
          )}

          {isEditingNote && (
            <textarea
              ref={noteInputRef}
              className={styles.noteInput}
              value={node.note ?? ""}
              onChange={handleNoteChange}
              onBlur={handleNoteBlur}
              onKeyDown={handleNoteKeyDown}
              placeholder="Add notes…"
              rows={2}
            />
          )}

          {!node.note && !isEditingNote && (
            <button type="button" className={styles.addNoteHint} onClick={startEditingNote}>
              <NoteIcon />
              <span>Add notes…</span>
            </button>
          )}
        </div>

        <div className={`${styles.actions} ${showActions || showOverflow ? styles.visible : ""}`}>
          <button
            type="button"
            className={styles.quickAction}
            onClick={() => onAddSibling(node.id)}
            title={depth === 0 ? "Add section below" : "Add point below"}
            aria-label={depth === 0 ? "Add section below" : "Add point below"}
          >
            <PlusIcon />
          </button>
          {(totalNodeCount ?? 0) > 1 && (
            <button
              type="button"
              className={`${styles.quickAction} ${styles.quickActionDanger}`}
              onClick={() => {
                if (hasChildren) {
                  // Open the overflow menu and show delete confirmation for nodes with children
                  setShowOverflow(true);
                  setShowDeleteConfirm(true);
                } else {
                  onDelete(node.id);
                }
              }}
              title="Delete"
              aria-label="Delete"
            >
              <TrashIcon />
            </button>
          )}
          <div className={styles.overflowWrapper} ref={overflowRef}>
            <button
              type="button"
              className={`${styles.actionButton} ${showOverflow ? styles.overflowActive : ""}`}
              onClick={() => {
                setShowOverflow((v) => {
                  if (v) setShowDeleteConfirm(false);
                  return !v;
                });
              }}
              title="More actions"
              aria-expanded={showOverflow}
              aria-haspopup="menu"
            >
              <MoreIcon />
            </button>
            {showOverflow && (
              <div className={styles.overflowMenu} role="menu">
                <button
                  type="button"
                  className={styles.overflowItem}
                  onClick={() => {
                    onAddSibling(node.id);
                    setShowOverflow(false);
                  }}
                  role="menuitem"
                >
                  <PlusIcon />
                  <span>{depth === 0 ? "Add section below" : "Add point below"}</span>
                  <kbd className={styles.shortcutHint}>Enter</kbd>
                </button>
                <button
                  type="button"
                  className={styles.overflowItem}
                  onClick={() => {
                    onAddChild(node.id);
                    setShowOverflow(false);
                  }}
                  role="menuitem"
                >
                  <IndentIcon />
                  <span>{depth === 0 ? "Add sub-point" : "Add detail"}</span>
                </button>
                <div className={styles.overflowDivider} role="separator" />
                {onExpandPoint && node.content.trim() && (
                  <button
                    type="button"
                    className={styles.overflowItem}
                    onClick={() => {
                      onExpandPoint(node.id);
                      setShowOverflow(false);
                    }}
                    disabled={isExpanding}
                    role="menuitem"
                  >
                    {isExpanding ? <SpinnerIcon /> : <ExpandIcon />}
                    <span>Expand with AI</span>
                  </button>
                )}
                <button
                  type="button"
                  className={styles.overflowItem}
                  onClick={() => {
                    onIndent(node.id);
                    setShowOverflow(false);
                  }}
                  role="menuitem"
                >
                  <IndentRightIcon />
                  <span>Indent</span>
                  <kbd className={styles.shortcutHint}>Tab</kbd>
                </button>
                {depth > 0 && (
                  <button
                    type="button"
                    className={styles.overflowItem}
                    onClick={() => {
                      onOutdent(node.id);
                      setShowOverflow(false);
                    }}
                    role="menuitem"
                  >
                    <OutdentIcon />
                    <span>Outdent</span>
                    <kbd className={styles.shortcutHint}>⇧Tab</kbd>
                  </button>
                )}
                <button
                  type="button"
                  className={styles.overflowItem}
                  onClick={() => {
                    onMoveUp(node.id);
                    setShowOverflow(false);
                  }}
                  role="menuitem"
                >
                  <MoveUpIcon />
                  <span>Move Up</span>
                  <kbd className={styles.shortcutHint}>Alt↑</kbd>
                </button>
                <button
                  type="button"
                  className={styles.overflowItem}
                  onClick={() => {
                    onMoveDown(node.id);
                    setShowOverflow(false);
                  }}
                  role="menuitem"
                >
                  <MoveDownIcon />
                  <span>Move Down</span>
                  <kbd className={styles.shortcutHint}>Alt↓</kbd>
                </button>
                <div className={styles.overflowDivider} role="separator" />
                {showDeleteConfirm ? (
                  <div className={styles.deleteConfirm} role="alert">
                    <p className={styles.deleteConfirmText}>
                      {hasChildren
                        ? `Delete this item and its ${node.children!.length} sub-item${node.children!.length === 1 ? "" : "s"}?`
                        : "Delete this section?"}
                    </p>
                    <div className={styles.deleteConfirmActions}>
                      <button
                        type="button"
                        className={`${styles.overflowItem} ${styles.overflowItemDanger}`}
                        onClick={() => {
                          onDelete(node.id);
                          setShowOverflow(false);
                          setShowDeleteConfirm(false);
                        }}
                        role="menuitem"
                      >
                        <TrashIcon />
                        <span>{hasChildren ? "Yes, delete all" : "Yes, delete"}</span>
                      </button>
                      <button
                        type="button"
                        className={styles.overflowItem}
                        onClick={() => setShowDeleteConfirm(false)}
                        role="menuitem"
                      >
                        <span>Cancel</span>
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    className={`${styles.overflowItem} ${styles.overflowItemDanger}`}
                    onClick={() => {
                      if (isEmpty) {
                        onDelete(node.id);
                      } else {
                        setShowDeleteConfirm(true);
                      }
                    }}
                    role="menuitem"
                  >
                    <TrashIcon />
                    <span>Delete</span>
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const NOTE_TRUNCATE_LENGTH = 200;

function CollapsibleNote({ text, onEdit }: { text: string; onEdit?: () => void }) {
  const [expanded, setExpanded] = useState(false);
  const isTruncatable = text.length > NOTE_TRUNCATE_LENGTH;

  const toggle = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setExpanded((v) => !v);
  }, []);

  const displayText =
    isTruncatable && !expanded
      ? text.slice(0, NOTE_TRUNCATE_LENGTH).replace(/\s+\S*$/, "") + "…"
      : text;

  return (
    <div className={`${styles.note} ${isTruncatable ? styles.noteTruncatable : ""}`}>
      <NoteIcon />
      <span
        className={`${styles.noteText} ${onEdit ? styles.noteTextEditable : ""}`}
        onClick={onEdit}
        role={onEdit ? "button" : undefined}
        tabIndex={onEdit ? 0 : undefined}
        onKeyDown={
          onEdit
            ? (e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onEdit();
                }
              }
            : undefined
        }
      >
        {displayText}
      </span>
      {isTruncatable && (
        <button
          type="button"
          className={styles.noteToggle}
          onClick={toggle}
          aria-label={expanded ? "Show less" : "Show more"}
        >
          {expanded ? "Show less" : "Show more"}
        </button>
      )}
    </div>
  );
}

function GripIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="4" cy="2.5" r="1" fill="currentColor" />
      <circle cx="8" cy="2.5" r="1" fill="currentColor" />
      <circle cx="4" cy="6" r="1" fill="currentColor" />
      <circle cx="8" cy="6" r="1" fill="currentColor" />
      <circle cx="4" cy="9.5" r="1" fill="currentColor" />
      <circle cx="8" cy="9.5" r="1" fill="currentColor" />
    </svg>
  );
}

function ChevronIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M4 4.5L6 6.5L8 4.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IndentIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M2 4H12M6 7H12M2 10H12M2 6L4 7L2 8"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M7 2V12M2 7H12"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ExpandIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M7 1V3M7 11V13M1 7H3M11 7H13M2.75 2.75L4.17 4.17M9.83 9.83L11.25 11.25M11.25 2.75L9.83 4.17M4.17 9.83L2.75 11.25"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function NoteIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M10 6V10H2V2H6M8 1V5M6 3H10"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M1.75 3.5H12.25M5.25 3.5V2.33C5.25 1.87 5.62 1.5 6.08 1.5H7.92C8.38 1.5 8.75 1.87 8.75 2.33V3.5M10.5 3.5V11.67C10.5 12.13 10.13 12.5 9.67 12.5H4.33C3.87 12.5 3.5 12.13 3.5 11.67V3.5H10.5Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function MoreIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="3" cy="7" r="1.25" fill="currentColor" />
      <circle cx="7" cy="7" r="1.25" fill="currentColor" />
      <circle cx="11" cy="7" r="1.25" fill="currentColor" />
    </svg>
  );
}

function IndentRightIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M3 8H21M3 16H21M7 12L11 8M7 12L11 16"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function OutdentIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M3 8H21M3 16H21M11 12L7 8M11 12L7 16"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function MoveUpIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M12 19V5M12 5L5 12M12 5L19 12"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function MoveDownIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M12 5V19M12 19L5 12M12 19L19 12"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function SpinnerIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 14 14"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={styles.spinner}
    >
      <path
        d="M7 1V3M7 11V13M1 7H3M11 7H13"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
